/**
 * HandoffForm — modal-less form to initiate a cross-department handoff.
 *
 * Wraps in a Dialog when used from the department detail page (single
 * primary action), but the form itself is reusable. Validation mirrors
 * the gateway's Zod schema; submit posts to /api/handoffs which writes
 * an audit row + paired queue item atomically.
 */
import {
  type DepartmentSummary,
  type Handoff,
  type HandoffContract,
  type HandoffRequest,
  handoffRequestSchema,
} from '@chaos/types/department';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';

import { ApiError } from '../../lib/api/client';
import { departmentsApi, departmentsKeys } from '../../lib/api/departments';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';

interface HandoffFormProps {
  /** Departments available as target. The source dept is preselected. */
  departments: readonly DepartmentSummary[];
  sourceKey: string;
  trigger: React.ReactNode;
  /** Called after a successful submit. The handoff row is supplied. */
  onSubmitted?: (handoff: Handoff) => void;
}

export function HandoffForm({ departments, sourceKey, trigger, onSubmitted }: HandoffFormProps) {
  const [open, setOpen] = useState(false);
  const qc = useQueryClient();

  const contractsQ = useQuery({
    queryKey: departmentsKeys.handoffContracts(),
    queryFn: () => departmentsApi.listHandoffContracts(),
    enabled: open,
  });

  const form = useForm<HandoffRequest>({
    resolver: zodResolver(handoffRequestSchema),
    defaultValues: {
      source: sourceKey,
      target: '',
      contractName: '',
      title: '',
      payload: {},
      priority: 'normal',
    },
  });

  const target = form.watch('target');
  const eligibleContracts = (contractsQ.data ?? []).filter(
    (c: HandoffContract) => c.source === sourceKey && (!target || c.target === target),
  );

  const mutation = useMutation({
    mutationFn: (input: HandoffRequest) => departmentsApi.submitHandoff(input),
    onSuccess: (handoff) => {
      qc.invalidateQueries({ queryKey: departmentsKeys.list() });
      qc.invalidateQueries({
        queryKey: [...departmentsKeys.all, 'queue', handoff.target] as const,
      });
      setOpen(false);
      form.reset({
        source: sourceKey,
        target: '',
        contractName: '',
        title: '',
        payload: {},
        priority: 'normal',
      });
      onSubmitted?.(handoff);
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : 'Unable to submit the handoff.';
      form.setError('root', { message: msg });
    },
  });

  const onSubmit = form.handleSubmit((values) => {
    mutation.mutate(values);
  });

  const targetOptions = departments.filter((d) => d.key !== sourceKey);
  const rootError = form.formState.errors.root?.message;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Send a handoff</DialogTitle>
          <DialogDescription>
            Push work from this department into another. The target's standing queue receives a new
            item.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="flex flex-col gap-3 pt-1">
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <Label className="text-[12px]">From</Label>
              <Input value={sourceKey} readOnly className="bg-muted/50 text-muted-foreground" />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="handoff-target" className="text-[12px]">
                To
              </Label>
              <Select
                value={target}
                onValueChange={(v) => {
                  form.setValue('target', v, { shouldValidate: true });
                  // Reset contract when target changes.
                  form.setValue('contractName', '', { shouldValidate: true });
                }}
              >
                <SelectTrigger id="handoff-target">
                  <SelectValue placeholder="Pick target" />
                </SelectTrigger>
                <SelectContent>
                  {targetOptions.map((d) => (
                    <SelectItem key={d.key} value={d.key}>
                      {d.displayName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="handoff-contract" className="text-[12px]">
              Contract
            </Label>
            <Select
              value={form.watch('contractName')}
              onValueChange={(v) => form.setValue('contractName', v, { shouldValidate: true })}
              disabled={!target}
            >
              <SelectTrigger id="handoff-contract">
                <SelectValue placeholder={target ? 'Pick a contract' : 'Pick a target first'} />
              </SelectTrigger>
              <SelectContent>
                {eligibleContracts.length === 0 ? (
                  <div className="px-2 py-1.5 text-[12px] text-muted-foreground">
                    No registered contracts.
                  </div>
                ) : (
                  eligibleContracts.map((c) => (
                    <SelectItem key={c.name} value={c.name}>
                      {c.name}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="handoff-title" className="text-[12px]">
              Title
            </Label>
            <Input
              id="handoff-title"
              {...form.register('title')}
              placeholder="Q3 launch landing page brief"
              autoFocus
            />
            {form.formState.errors.title ? (
              <span className="text-[11px] text-destructive">
                {form.formState.errors.title.message}
              </span>
            ) : null}
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="handoff-payload" className="text-[12px]">
              Payload (JSON, optional)
            </Label>
            <Textarea
              id="handoff-payload"
              placeholder='{"brief": "Hero + 3 sections"}'
              rows={3}
              onChange={(e) => {
                const raw = e.target.value.trim();
                if (!raw) {
                  form.setValue('payload', {});
                  form.clearErrors('payload');
                  return;
                }
                try {
                  form.setValue('payload', JSON.parse(raw));
                  form.clearErrors('payload');
                } catch {
                  form.setError('payload', { message: 'Invalid JSON.' });
                }
              }}
            />
            {form.formState.errors.payload?.message ? (
              <span className="text-[11px] text-destructive">
                {String(form.formState.errors.payload.message)}
              </span>
            ) : null}
          </div>

          {rootError ? <p className="text-[12px] text-destructive">{rootError}</p> : null}

          <DialogFooter className="mt-2 gap-2">
            <Button type="button" variant="ghost" size="sm" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              size="sm"
              disabled={mutation.isPending || !form.formState.isValid}
            >
              {mutation.isPending ? 'Sending…' : 'Send handoff'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
